import * as turf from '@turf/turf';
import { ICumplimientoRecorridoVivo, IRecorrido } from 'modelos/src';

/**
 * Arma las features para pintar la traza de un recorrido partida en dos: los
 * tramos que el vehículo ya cubrió y los que le faltan.
 *
 * El backend guarda la cobertura como un bitmap de tramos ("buckets") de largo
 * fijo sobre la traza, así que acá hay que hacer el camino inverso: decodificar
 * el bitmap, agrupar los tramos consecutivos que están en el mismo estado y
 * cortar la LineString en esos puntos.
 */

/**
 * Lee el bit `indice` de un bitmap de Redis. Redis numera los bits de más
 * significativo a menos dentro de cada byte: el bit 0 es el MSB del byte 0.
 */
function leerBit(bytes: Uint8Array, indice: number): boolean {
  const byte = bytes[indice >> 3];
  if (byte === undefined) return false;
  return (byte & (0b1000_0000 >> (indice & 7))) !== 0;
}

function decodificarBase64(base64: string): Uint8Array {
  const binario = atob(base64);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) {
    bytes[i] = binario.charCodeAt(i);
  }
  return bytes;
}

interface ITramo {
  desdeBucket: number;
  hastaBucket: number; // inclusivo
  cubierto: boolean;
}

/**
 * Agrupa buckets consecutivos con el mismo estado. Sin esto habría que cortar la
 * traza una vez por bucket (hasta 250 cortes) en vez de una por cambio de estado.
 */
function agruparTramos(bytes: Uint8Array, cantidadBuckets: number): ITramo[] {
  const tramos: ITramo[] = [];
  let actual: ITramo | undefined;

  for (let i = 0; i < cantidadBuckets; i++) {
    const cubierto = leerBit(bytes, i);
    if (actual && actual.cubierto === cubierto) {
      actual.hastaBucket = i;
      continue;
    }
    actual = { desdeBucket: i, hastaBucket: i, cubierto };
    tramos.push(actual);
  }
  return tramos;
}

/**
 * Features de la traza partidas por estado de cobertura. Devuelve `[]` si falta
 * algo para poder dibujarlas (recorrido sin traza, medición sin bitmap).
 *
 * Cada feature lleva `cubierto` en las properties, y el mapa usa dos capas
 * filtradas por ese valor: no se puede hacer con una sola capa porque
 * `line-dasharray` de Mapbox no acepta expresiones data-driven.
 */
export function featuresCumplimientoRecorrido(
  recorrido: IRecorrido | undefined,
  cumplimiento: ICumplimientoRecorridoVivo | null | undefined,
  colorCubierto: string,
): GeoJSON.Feature[] {
  const coordinates = recorrido?.geojson?.coordinates as
    | [number, number][]
    | undefined;
  const cantidadBuckets = cumplimiento?.cantidadBuckets;
  if (
    !coordinates ||
    coordinates.length < 2 ||
    !cantidadBuckets ||
    !cumplimiento?.segmentosCubiertos
  ) {
    return [];
  }

  const linea = turf.lineString(coordinates);
  const largoTotalKm = turf.length(linea, { units: 'kilometers' });
  if (!largoTotalKm) return [];

  // El largo del bucket se deriva del largo que mide turf y no del que calculó el
  // backend: son fórmulas distintas y una diferencia de metros correría todos los
  // cortes. Lo que importa es que los N buckets cubran la traza completa.
  const largoBucketKm = largoTotalKm / cantidadBuckets;

  const bytes = decodificarBase64(cumplimiento.segmentosCubiertos);
  const tramos = agruparTramos(bytes, cantidadBuckets);

  const features: GeoJSON.Feature[] = [];
  for (const tramo of tramos) {
    const desdeKm = tramo.desdeBucket * largoBucketKm;
    const hastaKm = Math.min(
      (tramo.hastaBucket + 1) * largoBucketKm,
      largoTotalKm,
    );
    if (hastaKm <= desdeKm) continue;

    try {
      const pedazo = turf.lineSliceAlong(linea, desdeKm, hastaKm, {
        units: 'kilometers',
      });
      features.push({
        type: 'Feature',
        geometry: pedazo.geometry,
        properties: {
          id: recorrido._id,
          nombre: recorrido.nombre,
          cubierto: tramo.cubierto,
          color: tramo.cubierto ? recorrido.color || colorCubierto : '#9e9e9e',
        },
      });
    } catch {
      // Un tramo que turf no puede cortar (traza con vértices repetidos en ese
      // punto) no debería tirar abajo el dibujo del resto.
      continue;
    }
  }

  return features;
}
